import request from "@/api";
import { BaseService } from "@/api/services/baseService";
import type { Customer, CustomerReceivingAddress, CustomerVo, Page, CustomerInfoForm } from "@zhihaoscm/entity";
import { type CustomerExportParamsParams } from "@zhihaoscm/entity";
import { confirmable } from "@/utils/decorator/confirmable";
import type { CustomerVoListParams, CustomerSelectorParam } from "@/entity/manageCenter/customer";

// 客户
// 客户服务的分页实体是 CustomerVo，避免沿用商品实体导致 BaseService 泛型不匹配。
class CustomerService extends BaseService<CustomerVo, CustomerVo, unknown, unknown> {
    /**
     * 下拉选框列表数据
     */
    public async customerSelector(params: CustomerSelectorParam): Promise<Array<Customer>> {
        const { data } = await this.service.get(`${this.path}/selector`, {
            params
        });
        return data;
    }

    /**
     * 选择链云用户
     * @param params searchParam 用户账号
     */
    public async search(params: { searchParam: string }): Promise<Array<Customer>> {
        const { data } = await this.service.get(`${this.path}/search`, { params });
        return data;
    }

    /**启用或禁用客户信息 */
    @confirmable({
        msgs: ["是否禁用，禁用后该用户不可登录", "是否启用，启用后该用户可以登录"]
    })
    public async updateState(id: number, state: number): Promise<object> {
        return await this.service.put(`${this.path}/update/${id}/${state}`);
    }
    @confirmable("是否删除，删除后用户登录小程序重新进入小程序需重新注册账号")
    public async delete(id: number | string) {
        const { data } = await this.service.delete(`${this.path}/${id}`);
        return data;
    }
    public async paging(params: CustomerVoListParams): Promise<Page<CustomerVo>> {
        const { data } = await this.service.request({
            method: "get",
            url: `${this.path}/paging`,
            params: params
        });
        return data;
    }

    /**根据ID查询客户信息 */
    public async find(id: number): Promise<CustomerVo> {
        const { data } = await this.service.get(`${this.path}/find/${id}`);
        return data as CustomerVo;
    }
    // 查询客户下拉列表-船运需求
    public async shippingSelector(params: {
        searchParam?: string;
        state?: number;
        isReal?: boolean;
        pageSize?: number;
    }): Promise<CustomerVo[]> {
        const { data } = await this.service.get(`${this.path}/shipping/selector`, { params });
        return data;
    }

    //根据客户id查询地址
    public async findAddressByCustomerId(customerId: number): Promise<CustomerReceivingAddress[]> {
        const { data } = await this.service.get(`${this.path}/receiving-address/findByCustomerId/${customerId}`);
        return data;
    }

    //根据客户id查询子账号
    public async subAccountPaging(params: {
        mainAccountId: number;
        page: number;
        size: number;
        state: number;
    }): Promise<CustomerReceivingAddress[]> {
        const { data } = await this.service.get(`${this.path}/sub/paging`, { params });
        return data;
    }

    // 解除机构认证
    @confirmable("是否解除该用户的组织机构认证")
    public async cancelApply(id: number): Promise<object> {
        return await this.service.put(`${this.path}/cancelApply/${id}`);
    }

    // 恢复账号
    @confirmable("是否恢复")
    public async restore(id: number): Promise<object> {
        return await this.service.put(`${this.path}/restore/${id}`);
    }

    // 接口文档目前有问题，稍后补充
    public async getCustomerFollowPaging(params: unknown): Promise<Page<CustomerVo>> {
        const { data } = await this.service.get(`${this.path}/follow/paging`, { params });
        return data;
    }

    // 修改用户沟通记录-用户信息
    @confirmable("是否提交")
    public async updateCustomerInfo(id: number, form: CustomerInfoForm) {
        await this.service.put(`${this.path}/update/communicate-user/${id}`, form);
    }

    // 导出用户
    public async exportCustomer(params: CustomerExportParamsParams, filename: string) {
        const response = (await this.service.get(`${this.path}/exportCustomer`, {
            params,
            responseType: "blob"
        })) as Blob;
        const url = window.URL.createObjectURL(response);
        const link = document.createElement("a");

        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        return response;
    }

    // 导出沟通记录
    public async exportCustomerCommunicate(params: CustomerExportParamsParams, filename: string) {
        const response = (await this.service.get(`${this.path}/export`, {
            params,
            responseType: "blob"
        })) as Blob;
        const url = window.URL.createObjectURL(response);
        const link = document.createElement("a");

        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        return response;
    }

    // 获取已选用户的沟通记录
    public async getSelectedCustomerCommunicate(params: CustomerExportParamsParams) {
        return await this.service.get(`${this.path}/communicate-record`, { params });
    }
}
export const customerService = new CustomerService("/admin/customer", request.service);
